import { NAV_GROUPS } from "./consts";
import { SidebarNavGroup } from "./SidebarNavGroup";
import { cn } from "~/lib/utils";
import { SidebarCollapseButton } from "./SidebarCollapseButton";
import { trackCollapseSidebar, trackExpandSidebar } from "./analytics";
import { Footer } from "../Footer";
import { RegisterAssetCTA } from "./RegisterAssetCTA";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { useLayoutEffect, useRef } from "react";

// // // //

// NOTE - SidebarLayout is rendered inside each page component (see src/pages/*) rather than
// persisted in _app.tsx, so React unmounts + remounts the entire sidebar on every client-side
// navigation and the nav list's scroll position is lost. We stash the last scrollTop in module
// scope so the next mount can restore it. This survives navigation, not a full page reload.
let lastNavScrollTop = 0;

interface Props {
    pathname: string;
    showFooter?: boolean;
    collapsed?: boolean;
    children: React.ReactNode;
}

export function SidebarLayout(props: Props) {
    return (
        <SidebarProvider collapsed={props.collapsed}>
            <SidebarLayoutInner {...props} />
        </SidebarProvider>
    );
}

function SidebarLayoutInner(props: Props) {
    const { children, pathname, showFooter = true } = props;

    // const ref = useRef<HTMLDivElement>(null);

    // // // //
    // // // //
    // NOTE - we've disabled the collapable behavior ENTIRELY, for now.
    // This is a much bigger headache than originally anticipated - see article here describing the same problem:
    // https://plainenglish.io/blog/how-to-solve-hydration-error-in-next-js
    // NOTE - 1/26/24 - we may be able to handle this by storing the state in Redux? Same with the scroll-to enhancement below
    // For now, lets just assume the Sidebar is *always* expanded
    // const [collapsed, setCollapsed] = useState(!!props.collapsed);

    // NOTE - we now use the SidebarProvider to manage the collapsed state
    const { isCollapsed, setIsCollapsed } = useSidebar();

    // Restore the nav list scroll position from the previous mount (see lastNavScrollTop above)
    const navScrollRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        if (navScrollRef.current) {
            navScrollRef.current.scrollTop = lastNavScrollTop;
        }
    }, []);

    // // Use LocalStorage for caching the last expanded/collapsed state
    // // NOTE - this only reads/stores strings, so we use "true" + "false" strings to determine `collapsed` value below
    // const [collapsedLC, setCollapsedLC] = useLocalStorage<string>(
    //     "rwa-sidebar-expanded",
    //     "false",
    // );

    // const IS_CLIENT = typeof window !== undefined;

    // // Hook to store the current collapsed state (default value set by checking collapsedLC + IS_CLIENT)
    // const [collapsed, setCollapsedInternal] = useState(
    //     IS_CLIENT && collapsedLC === "true",
    // );

    // // Composite function that sets both internal collapsed state + LocalStorage value
    // function setCollapsed(newCollapsedValue: boolean) {
    //     setCollapsedInternal(newCollapsedValue);
    //     setCollapsedLC(String(newCollapsedValue)); // NOTE that we stringify the value here
    // }
    // // // //
    // // // //

    // // // //
    // ENHANCEMENT - scroll to active group

    // const activeNavGroups = NAV_GROUPS.filter((g) => {
    //     const hasActiveChild = areNavItemsActive({
    //         items: g.items,
    //         pathname,
    //     });

    //     return hasActiveChild;
    // });

    // useEffect(() => {
    //     if (ref.current === null) {
    //         return;
    //     }

    //     if (collapsed) {
    //         return;
    //     }

    //     if (activeNavGroups.length === 0) {
    //         return;
    //     }

    //     const activeNavGroupEl = document.getElementById(
    //         activeNavGroups[0].key,
    //     );
    //     if (!activeNavGroupEl) {
    //         return;
    //     }

    //     // Get the bounding rectangle of the active nav group element
    //     const activeNavGroupRect = activeNavGroupEl.getBoundingClientRect();
    //     // Get the bounding rectangle of the sidebar (ref.current)
    //     const sidebarRect = ref.current.getBoundingClientRect();

    //     // Calculate the top position to scroll to
    //     // This tries to center the activeNavGroupEl by adjusting the scrollTop value
    //     // It calculates the difference in scrollTop between the top of the activeNavGroupEl and the center of the sidebar
    //     // Then, it adjusts this value by half the height of the activeNavGroupEl to center it in the view
    //     const top =
    //         activeNavGroupEl.offsetTop -
    //         sidebarRect.height / 2 +
    //         activeNavGroupRect.height / 2;

    //     // Scroll to the calculated top position
    //     // Use a smooth scroll behavior if desired
    //     ref.current.scrollTo({ top: top, behavior: "smooth" });

    //     // Scroll to active group (V1)
    //     // ref.current.scrollTo({ top: activeNavGroupEl.scrollTop });
    // }, []);

    // // // //

    return (
        <>
            {/* Render Sidebar Layout */}
            <div
                className={cn(
                    "hidden md:fixed md:inset-y-0 md:flex md:flex-col select-none mt-nav-height bg-sidebar z-200 border-r shadow-xs",
                    {
                        "w-[var(--app-sidebar-width-collapsed)] items-center":
                            isCollapsed,
                        "md:w-[var(--app-sidebar-width)]": !isCollapsed,
                    },
                )}
            >
                <div
                    className={cn("flex h-full min-h-0 flex-col", {
                        "w-full items-center": isCollapsed,
                    })}
                >
                    <SidebarCollapseButton
                        collapsed={isCollapsed}
                        onToggle={() => {
                            // Track analytics events for expand/collapse
                            if (isCollapsed) {
                                trackExpandSidebar();
                            } else {
                                trackCollapseSidebar();
                            }

                            setIsCollapsed(!isCollapsed);
                        }}
                    />

                    <div
                        className={cn("flex min-h-0 flex-1 flex-col", {
                            "w-full items-center": isCollapsed,
                            "px-4 pt-4": !isCollapsed,
                        })}
                    >
                        {/* NOTE - scroll-fade replaces the gradient overlay we used to
                        position over this list - see shadcn/tailwind.css */}
                        <div
                            ref={navScrollRef}
                            onScroll={(e) => {
                                lastNavScrollTop = e.currentTarget.scrollTop;
                            }}
                            className="scroll-fade scroll-fade-24 flex-1 overflow-y-auto hide-scrollbar overflow-x-clip"
                        >
                            <div>
                                {NAV_GROUPS.map((g, idx) => (
                                    <SidebarNavGroup
                                        key={g.key}
                                        group={g}
                                        activePathname={pathname}
                                        collapsed={isCollapsed}
                                        groupIndex={idx}
                                    />
                                ))}
                            </div>
                        </div>

                        {!isCollapsed && (
                            <div className="-mx-4 shrink-0 z-20">
                                <RegisterAssetCTA />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Page Content */}
            <div
                className={cn("flex flex-1 flex-col", {
                    "md:pl-[var(--app-sidebar-width)]": !isCollapsed,
                    "md:pl-[var(--app-sidebar-width-collapsed)]": isCollapsed,
                })}
            >
                <main className="flex-1">
                    <div
                        className={cn(
                            "mx-auto flex flex-col min-h-[calc(100vh-theme(spacing.nav-height)-1px)]",
                        )}
                    >
                        <div className="flex-1">{children}</div>
                        {showFooter && (
                            <div className="mx-6">
                                <Footer />
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </>
    );
}
